import { Box, IconButton, useTheme } from '@mui/material';
import { ShareDialog } from '@src/App/Components/Buttons/ShareButton/ShareButton';
import { useDialog } from '@src/App/Components/Dialogs/DialogManager';
import { EIconCategory, getAppIcon } from '@src/helper/iconRegistry';
import type { Entity } from '@src/Objects/Entity';
import type React from 'react';

import { useNavigation } from '../Navigation/NavigationContext';
import MvpBreadcrumb from './MvpBreadcrumb';
import MvpQuickActions from './MvpQuickActions';
import MvpRoleSwitcher from './MvpRoleSwitcher';

export interface IMvpRole {
    id: string;
    label: string;
    icon: React.ReactNode;
}

export interface IMvpQuickAction {
    id: string;
    label: string;
    icon: React.ReactNode;
    onClick: () => void;
    variant?: 'default' | 'primary';
}

/**
 * MvpSubNav reads its config from NavigationContext (set by the current page via setSubNav).
 * Props are kept as optional overrides for cases where MvpSubNav is used standalone.
 */
interface IMvpSubNavProps {
    currentPageTitle?: string;
    roles?: IMvpRole[];
    activeRole?: string;
    onRoleChange?: (roleId: string) => void;
    quickActions?: IMvpQuickAction[];
    shareEntity?: Entity;
}

function MvpSubNav(props: IMvpSubNavProps) {
    const theme = useTheme();
    const { openDialog } = useDialog();
    const { subNav } = useNavigation();

    // Props override context — allows standalone usage without setSubNav
    const currentPageTitle = props.currentPageTitle ?? subNav?.currentPageTitle;
    const roles = props.roles ?? subNav?.roles;
    const activeRole = props.activeRole ?? subNav?.activeRole;
    const onRoleChange = props.onRoleChange ?? subNav?.onRoleChange;
    const quickActions = props.quickActions ?? subNav?.quickActions;
    const shareEntity = props.shareEntity ?? subNav?.shareEntity;

    const handleShare = () => {
        if (shareEntity) {
            openDialog(<ShareDialog id="MvpSubNavShareDialog" entity={shareEntity} resolve={() => {}} />);
        }
    };

    return (
        <Box
            sx={{
                position: 'relative',
                width: '100vw',
                left: '50%',
                right: '50%',
                marginLeft: '-50vw',
                marginRight: '-50vw',
                borderBottom: `1px solid ${theme.palette.divider}`,
                bgcolor: `${theme.palette.background.paper}E6`,
            }}
        >
            <Box
                sx={{
                    maxWidth: 'var(--var-wrapper-width)',
                    width: '100%',
                    margin: '0 auto',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: 1.5,
                    px: 3,
                    gap: 2,
                    flexWrap: { xs: 'wrap', md: 'nowrap' },
                    maxHeight: { lg: '50px' },
                    minHeight: { lg: '50px' },
                }}
            >
                <MvpBreadcrumb currentPageTitle={currentPageTitle} />

                <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 2 }}>
                    {quickActions && quickActions.length > 0 && <MvpQuickActions actions={quickActions} />}

                    {roles && roles.length > 0 && (
                        <MvpRoleSwitcher roles={roles} activeRole={activeRole} onRoleChange={onRoleChange} />
                    )}

                    {shareEntity && (
                        <IconButton
                            onClick={handleShare}
                            size="small"
                            sx={{
                                color: theme.palette.text.secondary,
                                '&:hover': {
                                    color: theme.palette.primary.main,
                                    bgcolor: `${theme.palette.primary.main}10`,
                                },
                            }}
                        >
                            {getAppIcon(EIconCategory.SHARE)}
                        </IconButton>
                    )}
                </Box>
            </Box>
        </Box>
    );
}

export default MvpSubNav;
